// Types for delivery detail view API response
// Based on openapi.json schema for /delivery/{intakeId}/detail

export interface DeliveryDetailSummary {
  clientName: string;
  clientCode: string;
  clientAddress: string;
  status: string;
  invoiceNumber: string | null;
}

export interface DeliveryDetailClientDetails {
  caseName: string | null;
  fileReferenceNumber: string | null;
  clientContactId: string;
  clientContactName: string;
  contactPhone: string | null;
  contactEmail: string | null;
  assignedUser: string | null;
  serviceType: string;
  createdBy: string;
  createdAt: string;
  modifiedBy: string | null;
  modifiedAt: string | null;
}

export interface DeliveryDetailPickupInformation {
  pickupName: string;
  pickupAddress: string;
  pickupCountyId: string;
  pickupDateTime: string;
  printing: string | null;
  pickupType: string;
  pickupTypeOther: string | null;
  numberOfItems: number;
  timeReady: string | null;
  pickupDriverId: string | null;
  pickupDriverName: string | null;
  pickupDriverPhone: string | null;
  pickupInstructions: string | null;
}

export interface DeliveryDetailDeliveryInformation {
  deliveryName: string;
  deliveryPhone: string | null;
  attention: string | null;
  deliveryAddress: string;
  deliveryCountyId: string;
  deliveryDateTime: string;
  deliveryDriverId: string | null;
  deliveryDriverName: string | null;
  deliveryDriverPhone: string | null;
  returnDate: string | null;
  deliveryInstructions: string | null;
}

export interface BillingItem {
  code: string;
  description: string | null;
  amount: number;
}

export interface DeliveryDetailBilling {
  billingItems: BillingItem[];
  totalBillingAmount: number;
}

export interface DeliveryUpdateCard {
  updateId: string;
  addedAt: string;
  addedBy: string;
  modifiedAt: string | null;
  modifiedBy: string | null;
  status: string;
}

export interface DeliveryUpdateEmailLog {
  emailId: string;
  sentAt: string;
  sentTo: string;
  sentBy: string;
  status?: 'SENT' | 'FAILED';
}

export interface DeliveryDetailUpdates {
  updates: DeliveryUpdateCard[];
  lastFiveDeliveryUpdateEmails: DeliveryUpdateEmailLog[];
  waitingTimeHourlyRate: number;
}

export interface InvoiceEmailLog {
  emailId: string;
  sentAt: string;
  sentTo: string;
  sentBy: string;
  status?: 'SENT' | 'FAILED';
}

export interface InvoiceCardDetail {
  invoiceNumber: string;
  invoiceStatus: string;
  totalBillingAmount: number;
  modifiedAt: string | null;
  modifiedBy: string | null;
}

export interface DeliveryDetailInvoice {
  invoice: InvoiceCardDetail;
  lastFiveInvoiceEmails: InvoiceEmailLog[];
}

export interface ActionFlags {
  canExportDeliverySheet: boolean;
  canEditIntakeForm: boolean;
  canEditWorkOrder: boolean;
  canEditDeliveryUpdates: boolean;
  canEmailDeliveryUpdates: boolean;
  canEmailInvoice: boolean;
}

// Common details shared between intake and work order
export interface CommonDetails {
  isPrepaid: boolean;
  deliveryTime: string | null;
  deliveryInstructions: string | null;
  dgrCheck: boolean;
  checkNumber: string | null;
  checkAmount: number | null;
  miscFee: number | null;
  additionalJobs: string[]; // default []
  additionalDeliveryDrivers: string[]; // default []
  caseId: string | null;
  assignedTeamId: string | null;
  intakeId: string;
  workOrderId: string;
  workOrderNumber: string | null;
  cancelledBy: { userId: string; name: string } | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
}

// Intake form detail response (no delivery updates or invoice sections)
export interface DeliveryIntakeDetailResponse {
  summary: DeliveryDetailSummary;
  clientDetails: DeliveryDetailClientDetails;
  pickupInformation: DeliveryDetailPickupInformation;
  deliveryInformation: DeliveryDetailDeliveryInformation;
  billing: DeliveryDetailBilling;
  actionFlags: ActionFlags;
  commonDetails: CommonDetails;
}

// Work order detail response (includes delivery updates and invoice)
export interface DeliveryWorkOrderDetailResponse {
  summary: DeliveryDetailSummary;
  clientDetails: DeliveryDetailClientDetails;
  pickupInformation: DeliveryDetailPickupInformation;
  deliveryInformation: DeliveryDetailDeliveryInformation;
  deliveryUpdates: DeliveryDetailUpdates;
  billing: DeliveryDetailInvoice;
  actionFlags: ActionFlags;
  commonDetails: CommonDetails;
}

// Union type for either response
export type DeliveryDetailResponse = DeliveryIntakeDetailResponse | DeliveryWorkOrderDetailResponse;

// Type guard to check if response is work order
export function isWorkOrderDetail(
  response: DeliveryDetailResponse
): response is DeliveryWorkOrderDetailResponse {
  return 'deliveryUpdates' in response;
}
